import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  addTrackerOrderEnquiry,
  getAllTrackerOrders,
  getMergedTrackerOrders,
  getTrackerEnquiriesByKey,
  getTrackerOrderDetails,
  searchTrackerEnquiriesByKey,
} from "../dispatcher/productTrackerDispatcher";

interface ProductTrackerState {
  loading: boolean;
  orders: any[];
  orderDetails: any[];
  trackedOrder: any;
  filteredOrders: any[]; // Separate filtered results
  loadingEnquiries: boolean;
  loadingAddEnqiry: boolean;
  enquiries: any;
  filteredEnquiries: any;
  sortDirection: string;
  isProductTrackerLoaded: boolean;
}

const initialState: ProductTrackerState = {
  loading: false,
  orders: [],
  trackedOrder: null,
  orderDetails: [],
  filteredOrders: [], // Maintain filtered orders separately
  loadingAddEnqiry: false,
  loadingEnquiries: false,
  enquiries: [],
  filteredEnquiries: [],
  sortDirection: "desc",
  isProductTrackerLoaded: false,
};

// Create a slice for actions and reducers
export const productTrackerSlice = createSlice({
  name: "productTracker",
  initialState,
  reducers: {
    searchTrackerOrders: (state, action: PayloadAction<string>) => {
      const searchTerms = action.payload.toLowerCase().trim().split(/\s+/).filter(Boolean);

      if (searchTerms.length === 0) {
        state.filteredOrders = state.orders;
        return;
      }

      state.filteredOrders = state.orders.filter((order: any) => {
        const orderNumber = order?.OrderNumber1?.toString()?.toLowerCase() || "";
        const salesperson = order?.Salesperson?.toString()?.toLowerCase() || "";
        const bpName = order?.BPName?.toString()?.toLowerCase() || "";
        const itemName = order?.ItemName?.toString()?.toLowerCase() || "";
        const itemCode = order?.ItemCode?.toString()?.toLowerCase() || "";

        return searchTerms.every(term => (
          orderNumber.includes(term) ||
          salesperson.includes(term) ||
          bpName.includes(term) ||
          itemName.includes(term) ||
          itemCode.includes(term)
        ));
      });
    },
    resetSearch: (state) => {
      state.filteredOrders = state.orders; // Reset to full orders list
    },
    resetEnquiries: (state) => {
      state.filteredEnquiries = state.enquiries; // Reset to full orders list
    },
    filterTrackerOrders: (state, action: PayloadAction<string>) => {
      const filterStatus = action.payload.toLowerCase();
      state.filteredOrders = state.orders.filter((order: any) => {
        const orderStatus = order?.OrderStatus
          ? String(order.OrderStatus).toLowerCase()
          : "";
        return orderStatus === filterStatus;
      });
    },
    filterTrackerOrdersMulti: (state, action: PayloadAction<string[]>) => {
      const filterStatuses = action.payload.map((s) => s.toLowerCase());
      state.filteredOrders = state.orders.filter((order: any) => {
        const orderStatus = order?.OrderStatus
          ? String(order.OrderStatus).toLowerCase()
          : "";
        return filterStatuses.includes(orderStatus);
      });
    },
    filterSpecificField: (
      state,
      action: PayloadAction<{ filterField: string; filterValue: any }>
    ) => {
      const { filterField, filterValue } = action.payload;
      state.filteredOrders = state.orders.filter((order: any) => {
        return order?.[filterField] === filterValue;
      });
    },

    sortTrackerOrdersByOrderNumber: (
      state,
      action: PayloadAction<"asc" | "desc">
    ) => {
      const direction = action.payload;

      state.filteredOrders = [...state.filteredOrders].sort(
        (a: any, b: any) => {
          const orderA = a?.OrderNumber1 ?? "";
          const orderB = b?.OrderNumber1 ?? "";

          if (orderA < orderB) return direction === "asc" ? -1 : 1;
          if (orderA > orderB) return direction === "asc" ? 1 : -1;
          return 0;
        }
      );
      state.sortDirection = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllTrackerOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllTrackerOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.data?.recordsets || [];
        state.filteredOrders = state.orders; // Initialize filtered orders
      })
      .addCase(getAllTrackerOrders.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getTrackerOrderDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTrackerOrderDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.orderDetails = action.payload.data?.recordsets || [];
      })
      .addCase(getTrackerOrderDetails.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getMergedTrackerOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMergedTrackerOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload || [];
        state.filteredOrders = state.orders;
        state.isProductTrackerLoaded = true;
      })
      .addCase(getMergedTrackerOrders.rejected, (state) => {
        state.loading = false;
      })
      .addCase(addTrackerOrderEnquiry.pending, (state) => {
        state.loadingAddEnqiry = true;
      })
      .addCase(addTrackerOrderEnquiry.fulfilled, (state, action) => {
        state.loadingAddEnqiry = false;
      })
      .addCase(addTrackerOrderEnquiry.rejected, (state) => {
        state.loadingAddEnqiry = false;
      })
      .addCase(getTrackerEnquiriesByKey.pending, (state) => {
        state.loadingEnquiries = true;
      })
      .addCase(getTrackerEnquiriesByKey.fulfilled, (state, action) => {
        state.loadingEnquiries = false;
        state.enquiries = action.payload;
        state.filteredEnquiries = state.enquiries;
      })
      .addCase(getTrackerEnquiriesByKey.rejected, (state) => {
        state.loadingEnquiries = false;
      })
      .addCase(searchTrackerEnquiriesByKey.pending, (state) => {
        state.loadingEnquiries = false;
      })
      .addCase(searchTrackerEnquiriesByKey.fulfilled, (state, action) => {
        state.loadingEnquiries = false;
        state.enquiries = action.payload;
        state.filteredEnquiries = state.enquiries;
      })
      .addCase(searchTrackerEnquiriesByKey.rejected, (state) => {
        state.loadingEnquiries = false;
      });
  },
});

// Export actions
export const {
  searchTrackerOrders,
  resetSearch,
  resetEnquiries,
  filterTrackerOrders,
  filterTrackerOrdersMulti,
  sortTrackerOrdersByOrderNumber,
  filterSpecificField,
} = productTrackerSlice.actions;

// Export reducer
export default productTrackerSlice.reducer;
